import { useEffect, useMemo, useRef, useState } from 'react'

import { createLocalAgentClient, createUploadMonitor, transportRequest, uploadSupported, type LocalAgentClient, type UploadMonitor, type UploadSnapshot } from '@krp/client-runtime'

import type { OwnDispatch } from '../../../state'
import { OWN_AGENT_URLS } from '../../agent-session/api/urls'
import { createTokenFetcher } from '../../agent-session/model/agentToken'
import type { AgentSession } from '../../agent-session/model/useAgentSession'
import { createDeliveryReader } from './deliveryReader'
import { deliveryViewNoAgent, deliveryViewOf, type DeliveryView } from './deliveryView'

const NEVER: UploadSnapshot = { phase: 'never', status: null, readAt: null, nextAt: null, failures: 0, failure: null }

export interface DeliveryMonitorView {
  view: DeliveryView
  /** «Проверить»: монитор не пустит запрос чаще порога, поэтому слишком частое нажатие безвредно. */
  refresh: () => void
}

/** Идёт ли денежная операция: чек, смена, наличные, выяснение исхода. Диагностика ей уступает. */
export const moneyBusyOf = (model: AgentSession['model']): boolean => model.busy !== null || model.pendingUnknown !== null

/**
 * Монитор доставки на этом компьютере (RP-OWN9). Читает состояние выгрузки агента редко и только пока агент на связи и объявил
 * выгрузку; не отправляет запрос, пока идёт денежная операция. Сессию агента даёт вызывающий (окно продажи уже держит её), чтобы
 * не заводить второй поиск агента по портам.
 */
export function useDeliveryMonitor(session: AgentSession, options: { dispatch: OwnDispatch; client?: LocalAgentClient }): DeliveryMonitorView {
  const { dispatch } = options
  const client = useMemo(() => options.client ?? createLocalAgentClient(), [options.client])
  const modelRef = useRef(session.model)
  useEffect(() => {
    modelRef.current = session.model
  })
  const [reading, setReading] = useState<{ key: string; snapshot: UploadSnapshot } | null>(null)
  const monitorRef = useRef<UploadMonitor | null>(null)

  const agent = session.model.agent
  const supported = agent !== null && uploadSupported(agent.hello.api)
  const port = agent?.port
  const instanceId = agent?.hello.instanceId
  const key = `${port ?? ''}:${instanceId ?? ''}`

  useEffect(() => {
    if (!supported || port === undefined || instanceId === undefined) {
      monitorRef.current = null
      return undefined
    }
    const read = createDeliveryReader({
      client,
      agent: () => {
        const current = modelRef.current.agent
        return current === null ? null : { port: current.port, instanceId: current.hello.instanceId }
      },
      fetchToken: createTokenFetcher((id) => dispatch(transportRequest({ url: OWN_AGENT_URLS.token, method: 'POST', body: { agentInstanceId: id } }))),
    })
    const monitor = createUploadMonitor({ read, isMoneyBusy: () => moneyBusyOf(modelRef.current), onChange: (snapshot) => setReading({ key, snapshot }) })
    monitorRef.current = monitor
    monitor.start()
    return () => {
      monitor.stop()
      if (monitorRef.current === monitor) monitorRef.current = null
    }
  }, [client, dispatch, supported, port, instanceId, key])

  // Ответ прежнего агента (другой порт или установка) не показывается как ответ нового.
  const snapshot = reading !== null && reading.key === key ? reading.snapshot : NEVER
  const view = agent === null ? deliveryViewNoAgent() : deliveryViewOf({ supported, snapshot })
  return { view, refresh: () => void monitorRef.current?.refresh() }
}
